import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { CaretDown, CircleNotch, X } from "@phosphor-icons/react"
import { SelectUI } from "@/components/ui/custom-select"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { FormError } from "@/components/ui/form"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchContent, SearchItem, SuggestionSearch } from "@/components/ui/search"
import { useClickOutside } from "@/hooks/useClickOutside"
import { cn } from "@/lib/utils"

export type OptionItem = {
  country?: string
  postalCode?: string
  city?: string
  id?: string
  code?: string
} & SelectUI

export type CommonSearchProps = {
  placeholder?: string
  value?: string
  onSelect: (item: OptionItem) => void
  error?: string
  onClear?: () => void
  isLoading?: boolean
  highlightOptions?: OptionItem[]
}

type AddressSearchProps = {
  setKeyword: (value: string) => void
  options: OptionItem[]
  className?: string
  type: "city" | "country" | "postalCode"
} & CommonSearchProps

const AddressSearch = ({
  placeholder,
  value = "",
  onSelect,
  error,
  setKeyword,
  options,
  className = "",
  type,
  onClear,
  isLoading = false,
  highlightOptions = [],
}: AddressSearchProps) => {
  const searchRef = useRef<HTMLInputElement>(null)
  const searchResultsRef = useRef<HTMLInputElement>(null)
  const [searchResultVisible, setSearchResultVisible] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const { t } = useTranslation()

  const handleSearchInputChange = (value: string) => {
    setKeyword(value)
    setSearchValue(value)
    if (value === "") {
      onClear?.()
    }
  }

  const handleKeyUp = () => {
    if (!searchResultVisible) {
      setKeyword(searchValue)
      setSearchResultVisible(true)
    }
  }

  const handleClearSearch = () => {
    onClear?.()
    setSearchValue("")
    setKeyword("")
    closeSearchResults()
  }

  const closeSearchResults = () => {
    setSearchResultVisible(false)
  }

  useClickOutside((event: MouseEvent) => {
    if (
      searchResultsRef.current &&
      event.target instanceof HTMLElement &&
      !searchResultsRef.current.contains(event.target)
    ) {
      closeSearchResults()
    }
  }, searchResultsRef)

  const handleSelect = (item: SelectUI) => {
    setSearchValue(item.value)
    onSelect(item)
    closeSearchResults()
    searchRef.current?.blur()
  }

  useEffect(() => {
    if (value || value === "") {
      setSearchValue(value)
    }
  }, [value])

  return (
    <SuggestionSearch className={cn(className)}>
      <div className="relative">
        {isLoading ? (
          <label
            data-testid={`${type}-search-label`}
            htmlFor={`${type}-'search'`}
            className="absolute top-1/2 right-1 -translate-y-1/2 p-2">
            <CircleNotch className="text-on-surface-subdued animate-spin" />
          </label>
        ) : (
          <label
            data-testid={`${type}-search-label`}
            htmlFor={`${type}-'search'`}
            className="absolute top-1/2 right-1 -translate-y-1/2 p-2">
            <CaretDown className="text-on-surface-subdued" />
          </label>
        )}
        <input
          id={`${type}-'search'`}
          ref={searchRef}
          value={searchValue}
          className={cn(
            "peer border-border bg-surface-subdued text-on-surface placeholder:text-on-surface-subdued hover:bg-surface-hovered focus:outline-primary active:bg-surface-pressed h-12 w-full rounded-md border pt-0 pr-23.75 pl-4 text-sm outline-offset-0 transition-all focus:outline-2 disabled:cursor-not-allowed",
            error && "outline-critical! outline-1 focus:outline-2",
          )}
          data-testid={`${type}-search-input`}
          autoComplete="off"
          aria-autocomplete="none"
          placeholder={placeholder}
          onChange={({ target }) => handleSearchInputChange(target.value)}
          onKeyUp={handleKeyUp}
          onClick={handleKeyUp}
        />
        {searchValue && (
          <span
            className="hover:bg-surface-hovered visible absolute top-1/2 right-14 -translate-y-1/2 cursor-pointer rounded-full p-1 transition-all"
            onClick={handleClearSearch}>
            <X className="text-on-surface-subdued h-6 w-6 cursor-pointer" />
          </span>
        )}
      </div>
      {error && <FormError>{t(error || "")}</FormError>}
      {searchResultVisible && (
        <SearchContent data-testid="country-search-group" ref={searchResultsRef}>
          {[...highlightOptions, ...options].length === 0 ? (
            <p className="text-on-surface-subdued cursor-default px-2 text-sm select-none">
              {t("common.nothingFound")}
            </p>
          ) : (
            <ScrollArea viewportClassName="max-h-64">
              <div className="space-y-0.75 py-2 text-left">
                {highlightOptions.map((option) => (
                  <SearchItemOption option={option} handleSelect={handleSelect} type={type} key={option.id} />
                ))}
                {highlightOptions.length > 0 && options.length > 0 && (
                  <DropdownMenuSeparator className="bg-border -mx-2 my-0" />
                )}
                {options.map((option) => (
                  <SearchItemOption option={option} handleSelect={handleSelect} type={type} key={option.id} />
                ))}
              </div>
            </ScrollArea>
          )}
        </SearchContent>
      )}
    </SuggestionSearch>
  )
}

type SearchItemOptionProps = {
  option: OptionItem
  handleSelect: AddressSearchProps["onSelect"]
  type: AddressSearchProps["type"]
}

const SearchItemOption = ({ option, handleSelect, type }: SearchItemOptionProps) => {
  const { label, value, city, postalCode, id } = option

  return (
    <SearchItem key={id} value={value} onSelect={() => handleSelect(option)} className="text-on-surface">
      {type === "country" ? (
        label
      ) : (
        <div className="grid grid-cols-1 py-0.5">
          <div
            className={cn(
              "text-body-sm block truncate",
              type === "city" ? "text-on-surface" : "text-on-surface-subdued order-1",
            )}>
            {city}
          </div>
          <div
            className={cn(
              "text-body-sm text-on-surface-subdued block truncate",
              type === "postalCode" ? "text-on-surface" : "text-on-surface-subdued order-1",
            )}>
            {postalCode}
          </div>
        </div>
      )}
    </SearchItem>
  )
}

export { AddressSearch }
